import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

/**
 * Buy Buttons Subscribe Component
 * Handles the subscription form within buy buttons block.
 * Submits the form via AJAX with the selected selling plan.
 * 
 * @typedef {object} BuyButtonsSubscribeRefs
 * @property {HTMLSelectElement} frequencySelect - The frequency dropdown
 * @property {HTMLInputElement} sellingPlanInput - The hidden selling plan input
 * 
 * @extends Component<BuyButtonsSubscribeRefs>
 */
export class BuyButtonsSubscribe extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  
  /** @type {boolean} */
  #inFlight = false;

  connectedCallback() {
    super.connectedCallback();
    
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // Set up form submit listener
    const form = this.querySelector('form');
    form?.addEventListener('submit', this.#handleSubmit, { signal });
    
    // Set up frequency select listener to update hidden input
    this.refs.frequencySelect?.addEventListener('change', this.#handleFrequencyChange, { signal });
    
    // Stop click events from bubbling up to product card (prevents navigation)
    this.addEventListener('click', this.#stopPropagation, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * Stop click events from bubbling up to parent elements
   * @param {Event} event
   */
  #stopPropagation = (event) => {
    event.stopPropagation();
  };

  /**
   * Handle frequency selection change
   */
  #handleFrequencyChange = () => {
    if (this.refs.frequencySelect && this.refs.sellingPlanInput) {
      this.refs.sellingPlanInput.value = this.refs.frequencySelect.value;
    }
  };

  /**
   * Handle form submission via AJAX
   * @param {SubmitEvent} event
   */
  #handleSubmit = async (event) => {
    event.preventDefault();
    
    if (this.#inFlight) return;
    
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    
    const submitButton = form.querySelector('button[type="submit"]');
    
    this.#inFlight = true;
    if (submitButton) submitButton.disabled = true;
    
    const formData = new FormData(form);
    
    // Ensure selling_plan is set from the select
    if (this.refs.frequencySelect) {
      formData.set('selling_plan', this.refs.frequencySelect.value);
    }
    
    // Get cart section IDs for updating cart drawer
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    if (cartItemComponentsSectionIds.length) {
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    }
    
    try {
      const fetchCfg = fetchConfig('javascript', { body: formData });
      
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'application/json',
        },
      });
      
      const result = await response.json();
      
      if (result.status) {
        // Error response
        console.error('Add to cart error:', result);
        this.dispatchEvent(
          new CartErrorEvent(form.id || '', result.message, result.description, result.errors)
        );
      } else {
        // Success - dispatch cart add event
        this.dispatchEvent(new CartAddEvent(result));
        
        // Update cart sections if available
        if (result.sections) {
          document.querySelectorAll('cart-items-component').forEach((cartComponent) => {
            if (cartComponent instanceof HTMLElement && cartComponent.dataset.sectionId) {
              const sectionId = cartComponent.dataset.sectionId;
              const sectionHtml = result.sections[sectionId];
              if (sectionHtml) {
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = sectionHtml;
                const newContent = tempDiv.querySelector('cart-items-component');
                if (newContent) {
                  cartComponent.innerHTML = newContent.innerHTML;
                }
              }
            }
          });
        }
        
        // Animate button to show success
        this.#animateSuccess(submitButton);
        
        // Open cart drawer if it exists
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.show === 'function') {
          cartDrawer.show();
        } else {
          // Try to trigger cart icon update
          const cartIcon = document.querySelector('cart-icon');
          if (cartIcon) {
            cartIcon.dispatchEvent(new CustomEvent('cart:refresh'));
          }
        }
      }
    } catch (error) {
      console.error('Subscribe form submission error:', error);
    } finally {
      this.#inFlight = false;
      if (submitButton) submitButton.disabled = false;
    }
  };

  /**
   * Animate button to show success state
   * @param {HTMLButtonElement | null} button
   */
  #animateSuccess(button) {
    if (!button) return;
    
    button.classList.add('atc-added');
    
    setTimeout(() => {
      button.classList.remove('atc-added');
    }, 2000);
  }
}

// Register the custom element
if (!customElements.get('buy-buttons-subscribe')) {
  customElements.define('buy-buttons-subscribe', BuyButtonsSubscribe);
}

export default BuyButtonsSubscribe;
